export const BILLING_INTERVALS = ['month', 'quarter', 'year']

const INTERVAL_COPY = {
  month: { name: 'Mensual', suffix: '/mes' },
  quarter: { name: 'Trimestral', suffix: '/trimestre' },
  year: { name: 'Anual', suffix: '/año' },
}

export function intervalCopy(interval) {
  return INTERVAL_COPY[interval] || INTERVAL_COPY.month
}

/**
 * Precios que el plan ofrece, en orden mensual, trimestral y anual.
 * Si el API no manda prices, queda solo el mensual.
 */
function tierList(price) {
  const raw = price && price.tiers
  const list = raw && Array.isArray(raw.tiers) ? raw.tiers : (Array.isArray(raw) ? raw : [])
  return list
    .map((tier) => ({
      members: Number(tier && tier.members),
      amount: Number(tier && tier.amount),
    }))
    .filter((tier) => Number.isFinite(tier.members) && tier.members > 0 && Number.isFinite(tier.amount))
    .sort((a, b) => a.members - b.members)
}

export function planBillingPrices(plan, monthlyFallback) {
  const list = plan && Array.isArray(plan.prices) ? plan.prices : []
  const planTiers = tierList({ tiers: plan && (plan.family_tiers || (plan.tiers && plan.tiers.tiers)) })
  const planExtra = Number(plan && plan.extra_member_amount)
  const normalized = list
    .map((price) => {
      const ownTiers = tierList(price)
      const extraRaw = price && price.tiers && price.tiers.extra_member
      const extra = Number(extraRaw)
      return {
        billing_interval: price && price.billing_interval,
        amount: Number(price && price.amount),
        tiers: ownTiers.length ? ownTiers : planTiers,
        extra_member: Number.isFinite(extra) ? extra : (Number.isFinite(planExtra) ? planExtra : 0),
      }
    })
    .filter((price) => BILLING_INTERVALS.includes(price.billing_interval) && Number.isFinite(price.amount))

  const ordered = BILLING_INTERVALS
    .map((interval) => normalized.find((price) => price.billing_interval === interval))
    .filter(Boolean)

  if (ordered.length) return ordered

  const monthly = Number(monthlyFallback)
  if (Number.isFinite(monthly)) {
    return [{ billing_interval: 'month', amount: monthly }]
  }
  return []
}

export function pickBillingPrice(prices, interval) {
  if (!prices || !prices.length) return null
  return prices.find((price) => price.billing_interval === interval) || prices[0]
}

/**
 * Monto del tramo Familiar para N personas. Sin tramo exacto, cobra el tramo mayor más extra_member por persona por encima.
 * @param {Object|null} price
 * @param {number} members
 * @returns {number}
 */
export function familyTierAmount(price, members) {
  const count = Number(members)
  const base = Number(price && price.amount)
  const tiers = (price && price.tiers) || []
  if (!Number.isFinite(count) || count < 2) {
    return Number.isFinite(base) ? base : 0
  }
  // Sin tramos el backend cobra amount × personas.
  if (!tiers.length) {
    return Number.isFinite(base) ? base * count : 0
  }
  const exact = tiers.find((tier) => tier.members === count)
  if (exact) return exact.amount
  // Igual que MembershipPlanPricing::amountFor: tramo mayor + extra por persona por encima de él.
  const highest = tiers[tiers.length - 1]
  const extra = Number(price.extra_member) || 0
  return highest.amount + extra * Math.max(0, count - highest.members)
}

function loggedUserId() {
  try {
    const raw = localStorage.getItem('userData')
    if (!raw) return null
    const user = JSON.parse(raw)
    return user && user.id != null ? Number(user.id) : null
  } catch (error) {
    return null
  }
}

/**
 * Cobro de la membresía. Usa billing del contrato y, si el API aún no lo manda,
 * lo arma con la suscripción (intervalo, cantidad y payer_user_id).
 * @param {Object|null} plan
 * @returns {Object|null}
 */
export function resolvedBilling(plan) {
  if (plan && plan.billing && typeof plan.billing === 'object') return plan.billing
  const subscription = plan && plan.subscription
  if (!subscription || typeof subscription !== 'object') return null

  const quantity = Number(subscription.quantity) || 1
  const unitRaw = subscription.unit_amount != null
    ? subscription.unit_amount
    : (subscription.price && subscription.price.amount)
  const unit = Number(unitRaw)
  const amountRaw = subscription.amount != null
    ? Number(subscription.amount)
    : (Number.isFinite(unit) ? unit * quantity : null)
  const userId = loggedUserId()
  let isPayer = true
  if (subscription.payer_user_id != null && userId != null) {
    isPayer = Number(subscription.payer_user_id) === userId
  } else if (plan.member_type && plan.member_type !== 'holder' && plan.member_type !== 'owner') {
    isPayer = false
  }

  return {
    billing_interval: subscription.billing_interval || null,
    quantity,
    unit_amount: Number.isFinite(unit) ? unit : null,
    amount: amountRaw,
    is_payer: isPayer,
  }
}

/**
 * Lo que realmente se cobra. Si no hay suscripción (venta en clínica), queda el precio del plan.
 * @param {Object|null} plan
 * @returns {number}
 */
export function chargeAmount(plan) {
  const billing = resolvedBilling(plan)
  if (billing && billing.amount != null && billing.amount !== '') {
    const amount = Number(billing.amount)
    if (Number.isFinite(amount)) return amount
  }
  const params = plan && plan.memberships_parameters
  if (params && params.price != null && params.price !== '') return Number(params.price) || 0
  if (plan && plan.total != null && plan.total !== '') return Number(plan.total) || 0
  return 0
}

/**
 * El integrante o empleado no paga: el API responde 403 a pagar, cancelar, reanudar y garantía.
 * Sin bloque billing (venta en clínica) se mantienen las acciones del panel.
 * @param {Object|null} plan
 * @returns {boolean}
 */
export function isBillingPayer(plan) {
  const billing = resolvedBilling(plan)
  if (!billing) return true
  return billing.is_payer !== false
}

export function billingSuffix(plan) {
  const billing = resolvedBilling(plan)
  const interval = billing && billing.billing_interval
  if (interval) return intervalCopy(interval).suffix
  return '/Mes'
}

export function planKindOf(plan) {
  if (!plan) return 'individual'
  const nested = plan.memberships_parameters && plan.memberships_parameters.plan_kind
  const kind = plan.plan_kind || nested
  if (kind) return String(kind)
  if (plan.catalogId === 'familiar') return 'family'
  if (plan.catalogId === 'empresas') return 'company'
  const name = String(plan.name || (plan.memberships_parameters && plan.memberships_parameters.name) || '').toLowerCase()
  if (name.includes('familiar') || name.includes('familia')) return 'family'
  if (name.includes('empresa')) return 'company'
  return 'individual'
}
